import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import Tabs, { Tab, TabPanel } from '@ingka/tabs';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { GetRetailUnitResponse } from '@lotta/shared/types-lottery-api';
import { swipeImageDomainWhitelistPattern } from '@lotta/shared/types-lottery-schemas';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styles from '../../../LotteryForm.module.scss';
import { ControlledTextArea } from '../../../components/controlled-text-area/ControlledTextArea';
import { mapRecord } from '../../../utils';
import { useFormModule } from '../../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../../types';
import { FindImageInfoTheatre } from '../../../components/find-image-info-theatre/FindImageInfoTheatre';

export const SimpleSignupTranslationSchema = Type.Object({
  title: Type.String({
    minLength: 1,
    maxLength: 100,
    errorMessage: {
      minLength: 'form-lottery-name-error-message-min-length',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
  body: Type.String({
    minLength: 1,
    maxLength: 1600,
    errorMessage: {
      minLength: 'form-lottery-name-error-message-min-length',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
  image: Type.String({
    format: 'uri',
    maxLength: 1000,
    pattern: swipeImageDomainWhitelistPattern,
    errorMessage: {
      format: 'form-error-message-uri',
      pattern: 'form-error-message-image-pattern',
      maxLength: 'form-lottery-name-error-message-max-length',
    },
  }),
});

export type SimpleSignupTranslationsPartialFormType = Static<
  typeof SimpleSignupTranslationsPartialFormSchema
>;
export const SimpleSignupTranslationsPartialFormSchema = Type.Object(
  {
    translation: Type.Record(
      Type.String(), // language code
      SimpleSignupTranslationSchema
    ),
  },
  { additionalProperties: true, $id: 'SimpleSignupTranslationsPartialFormSchema' }
);

export const SimpleSignupTranslationsTransformer: ModuleTransformer<SimpleSignupTranslationsPartialFormType> =
  {
    fromPayload: ({ translation }) => {
      return {
        translation: mapRecord(translation, ({ simpleSignup }) => ({
          title: simpleSignup?.title ?? '',
          body: simpleSignup?.body ?? '',
          image: simpleSignup?.image ?? '',
        })),
      };
    },
    toPayload: (formData) => {
      return {
        translation: mapRecord(formData.translation, (translation) => ({
          simpleSignup: translation,
        })),
      };
    },
  };

export type SimpleSignupTranslationsPartialFormProps =
  FormModuleProps<SimpleSignupTranslationsPartialFormType> & {
    retailUnitData: GetRetailUnitResponse['retailUnit'];
    moduleName: string;
  };

/**
 * Simple Signup translations (the language tabs part)
 *
 * This is split into its own form so that we can show an error message if any translations are
 * missing (validation fails) independent of other parts of the simple sign-up module.
 */
export const SimpleSignupTranslationsPartialForm: React.FC<
  SimpleSignupTranslationsPartialFormProps
> = ({ retailUnitData, moduleName, values, onChange, onValidation }) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { isValid },
  } = useFormModule({
    moduleName,
    defaultValues: values,
    schema: SimpleSignupTranslationsPartialFormSchema,
    onChange,
    onValidation,
  });

  const { language } = useLocale();

  const languageNames = new Intl.DisplayNames([language], { type: 'language' });
  const [currentTheatreTab, setCurrentTheatreTab] = useState('');

  const tabsAndPanels = retailUnitData.languages.map((language) => {
    const id = `${moduleName}-tab--${language}`;
    return {
      id,
      tab: <Tab key={language} text={languageNames.of(language)} tabPanelId={id} type="button" />,
      panel: (
        <TabPanel key={language} tabPanelId={id}>
          <ControlledTextArea
            control={control}
            labelKey="form-simple-signup-title-label"
            name={`translation.${language}.title`}
            showCharacterLimit
            upperLimit={SimpleSignupTranslationSchema.properties.title.maxLength}
          />
          <ControlledTextArea
            control={control}
            labelKey="form-simple-signup-body-label"
            name={`translation.${language}.body`}
            showCharacterLimit
            upperLimit={SimpleSignupTranslationSchema.properties.body.maxLength}
          />
          <ControlledTextArea
            control={control}
            labelKey="form-simple-signup-image-label"
            name={`translation.${language}.image`}
            showCharacterLimit
            upperLimit={SimpleSignupTranslationSchema.properties.image.maxLength}
            helpText={'https://www.ikea.com/ext/ingkadam/...'}
          />
          <FindImageInfoTheatre
            open={currentTheatreTab === id}
            onClose={() => setCurrentTheatreTab('')}
          />
          <Button
            text={t('find-image-theatre-button-text')}
            type="secondary"
            onClick={() => setCurrentTheatreTab(id)}
          />
        </TabPanel>
      ),
    };
  });

  const tabs = tabsAndPanels.map(({ tab }) => tab);
  const panels = tabsAndPanels.map(({ panel }) => panel);

  return (
    <form>
      <InlineMessage
        className={styles['inline-message']}
        variant="informative"
        title={`${t('form-explanation-message-simple-signup-translations-title')}`}
        body={`${t('form-explanation-message-simple-signup-translations-body')}`}
      />
      {!isValid && (
        <InlineMessage
          className={styles['inline-message']}
          variant="cautionary"
          title={`${t('form-missing-translations-title')}`}
          body={`${t('form-missing-translations-body')}`}
        />
      )}
      <Tabs tabs={tabs} tabPanels={panels} defaultActiveTab={tabsAndPanels[0].id} />
    </form>
  );
};
